import React, { useState, useEffect } from 'react';
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, Box } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Button } from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../../common/controls/LoadingSpinner';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useServices } from '../../../hooks/services/useServices';
import { useDeleteService } from '../../../hooks/services/useDeleteService';
import ServiceForm from '../forms/ServiceForm';
import { numberToCurrencyFormat } from '../../../utils/numberToCurrencyFormat';
import DefaultServicesSeederDialog from './DefaultServicesSeederDialog';
import { useHomeFab } from '../../../context/HomeFabContext';
import RequirePermission from '../../RequirePermission';
import { userHasAccess } from '../../../utils/userHasAccess';
import { SUB_MODULES } from '../../../constants/subModulesConstants';

export default function Services() {

    const { t } = useTranslation('common');
    const { showSnackbar } = useSnackbar();
    const { setDisableHomeFab } = useHomeFab();

    const [open, setOpen] = React.useState(false);
    const handleClickOpen = () => setOpen(true);
    const handleClose = () => {
      setOpen(false);
    };
    const [listTrigger, setListTrigger] = React.useState(false);
    const toggleListTrigger = () => {
      setListTrigger(prev => !prev);
    };

    const onEmptyData = () => {
      setSeederDialogOpen(true);
      setDisableHomeFab(true);
    };

    const onSeederDialogDone = () => {
      resetParent();
      setDisableHomeFab(false);
    };

    const { services, loading, loadMore, searchFunction, resetParent } = useServices(open, 1, 100, onEmptyData);

    const onDeleteSuccess = () => {
      resetParent();
    };

    const { remove } = useDeleteService(onDeleteSuccess);

    const [selectedService, setSelectedService] = useState(null);

    const [formOpen, setFormOpen] = React.useState(false);
    const [formMode, setFormMode] = React.useState("create");
    const [seederDialogOpen, setSeederDialogOpen] =  React.useState(false);

    const handleAdd = () => {
      setSelectedService(null);
      setFormMode("create");
      setFormOpen(true);
    };

    const handleEdit = (service) => {
      setSelectedService(service);
      setFormMode("edit");
      setFormOpen(true);
    };

    const onSaveSuccess = () => {
      resetParent();
      setFormOpen(false);
    };

    const handleDelete = async (service) => {
      try {
        await remove(service.id);
        showSnackbar(t('services.messages.delete_success', { name: service.name }), "success");
      } catch (err) {
        showSnackbar(t('services.messages.delete_fail', { name: service.name }), "error");
      }
    };

    return (
        <>
        <ListItem>
            <ListItemButton onClick={handleClickOpen}>
            <ListItemIcon>
              <LocalOfferIcon fontSize='large' color='secondary' />
            </ListItemIcon>
            <ListItemText 
              primary="Services Price List"
              secondary="view and manage services pricing"
            />
            </ListItemButton>
        </ListItem>

        <Dialog onClose={handleClose} open={open} disableRestoreFocus>
            <DialogTitle>
              Services Price List
            </DialogTitle>
            <DialogContent dividers>
              <List>
              {loading ? (
                <ListItem>
                  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    <LoadingSpinner />
                  </Box>
                </ListItem>
              ) : (
                  services.data.map((service) => (
                  <ListItem
                    key={service.id}
                    secondaryAction={
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <VerticalHamburgerMenu
                          options={[
                            { 
                              id: 1, 
                              name: 'Edit',
                              callback: () => handleEdit(service),
                              disabled: !userHasAccess(SUB_MODULES.SERVICES_PRICE_LIST, 2)
                            },
                            {
                              id: 2,
                              name: 'Delete',
                              callback: () => handleDelete(service),
                              confirmMessage: t('services.messages.delete_confirm', { name: service.name }),
                              disabled: !userHasAccess(SUB_MODULES.SERVICES_PRICE_LIST, 3)
                            },
                          ]}
                          />
                      </Box>
                    }
                  >
                      <ListItemButton onClick={() => {if (userHasAccess(SUB_MODULES.SERVICES_PRICE_LIST, 2)) handleEdit(service)}}>
                      <ListItemText
                        primary={service.name}
                        secondary={ 
                        <>
                          <Typography component="span" variant="body2" color="text.secondary">
                            {service.description || '(no description)'}
                          </Typography>
                          <br />
                          <Typography component="span" variant="body1" >
                            Price: {numberToCurrencyFormat(service.price)}
                          </Typography>
                          {service.disabled ?
                          <>
                          <br />
                          <Typography component="span" variant="body2" color="error">
                            (disabled)
                          </Typography>
                          </>
                          : null }
                        </>
                        }
                      />
                      </ListItemButton>
                  </ListItem>
                  ))
              )}
              </List>
            </DialogContent>
            <DialogActions>
              <RequirePermission subModuleName={SUB_MODULES.SERVICES_PRICE_LIST} minAccessLevel={2}>
                <Button onClick={handleAdd}>Add Service</Button>
              </RequirePermission>
              <div style={{flex: '1 0 0'}} />
              <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
        <ServiceForm
          open={formOpen}
          onClose={() => { setFormOpen(false)} }
          mode={formMode} 
          service={selectedService}
          successCallback={onSaveSuccess}
        />
        <DefaultServicesSeederDialog
            open={seederDialogOpen}
            setOpen={setSeederDialogOpen} 
            successCallback={onSeederDialogDone}
        />
        </>
    );
}